import { differenceInSeconds } from "date-fns"
import { useRef, useState } from "react"
import { Button } from "shared-web/components/base/control/Button"
import { BannerV2 } from "shared-web/components/base/informational/Banner"
import useKeyboardShortcut from "shared-web/hooks/useKeyboardShortcut"

import { useInterval } from "app/hooks"
import {
  DeprecatedModal,
  DeprecatedModalProps,
} from "components/base/overlay/Modal"
import { Cookies } from "lib/cookies/cookies"

interface SessionTimeoutModalProps extends DeprecatedModalProps {
  onStayLoggedIn: () => void
  isExtendingSession: boolean
  onSignOut: () => void
  isSigningOut: boolean
  showErrorBanner: boolean
}

export default function SessionTimeoutModal({
  open,
  onSignOut,
  isSigningOut,
  onStayLoggedIn,
  isExtendingSession,
  onClose,
  showErrorBanner,
}: SessionTimeoutModalProps) {
  const calculateSecondsUntilExpiration = () => {
    const { sessionExpiresDate } = Cookies.fromDocument(document)

    if (!sessionExpiresDate) {
      return 0
    }

    const now = new Date()
    return Math.max(differenceInSeconds(sessionExpiresDate, now), 0)
  }

  const [secondsUntilExpiration, setSecondsUntilExpiration] = useState(
    calculateSecondsUntilExpiration()
  )

  useInterval(
    () => {
      setSecondsUntilExpiration(calculateSecondsUntilExpiration())
    },
    open ? 1000 : null
  )

  const stayLoggedInButtonRef = useRef<HTMLButtonElement>(null)

  useKeyboardShortcut(
    "Enter",
    () => {
      stayLoggedInButtonRef.current?.click()
    },
    { disableOnModalOpen: false }
  )

  return (
    <DeprecatedModal
      onClose={onClose}
      open={open}
      initialFocus={stayLoggedInButtonRef}
    >
      <h2 className="font-bold">Session timeout</h2>
      {showErrorBanner && (
        <BannerV2
          severity="error"
          className="my-4"
          message="Something went wrong. Please try again."
        />
      )}
      {secondsUntilExpiration > 0 && (
        <p className="mb-4 mt-4 text-gray-600">
          You will be signed out in {secondsUntilExpiration}{" "}
          {secondsUntilExpiration === 1 ? "second" : "seconds"}.
        </p>
      )}
      <div className="flex flex-row justify-end sm:col-span-6">
        <Button
          id="timeout-logout-button"
          mode="secondary"
          loading={isSigningOut}
          onClick={onSignOut}
        >
          Sign out
        </Button>
        <Button
          id="timeout-stay-logged-in-button"
          mode="primary"
          className="ml-4"
          type="submit"
          loading={isExtendingSession}
          onClick={onStayLoggedIn}
          ref={stayLoggedInButtonRef}
        >
          Stay logged in
        </Button>
      </div>
    </DeprecatedModal>
  )
}
